/** Geofencing: the fence on each site, the policies, who they reach, and what punches did. */

export type GeofenceMode = 'UNRESTRICTED' | 'OWN_LOCATION' | 'SPECIFIC_LOCATIONS' | 'ANY_LOCATION'
export type GeofenceEnforcement = 'BLOCK' | 'FLAG'
export type GeofenceScope = 'EMPLOYEE' | 'DEPARTMENT' | 'LOCATION' | 'GRADE' | 'LEGAL_ENTITY'
export type GeofenceResult = 'ACCEPTED' | 'FLAGGED' | 'UNCHECKED' | 'NOT_REQUIRED' | 'OUTSIDE'
export type PhotoMode = 'OFF' | 'OPTIONAL' | 'REQUIRED'
export type FaceMatchMode = 'OFF' | 'FLAG' | 'BLOCK'
export type FaceStrictness = 'LOW' | 'MEDIUM' | 'HIGH'
export type UnenrolledAction = 'ENROL_REQUIRED' | 'ALLOW_FLAG'
export type FaceResult = 'MATCHED' | 'MISMATCH' | 'NO_FACE' | 'NOT_REGISTERED' | 'NOT_CHECKED' | 'NOT_REQUIRED'

export const PHOTO_MODES: Record<PhotoMode, string> = {
  OFF: 'No photo',
  OPTIONAL: 'Optional photo',
  REQUIRED: 'Photo required',
}

export const FACE_MATCH_MODES: Record<FaceMatchMode, { label: string; hint: string }> = {
  OFF: { label: 'Off', hint: 'The photo is kept as evidence but not compared.' },
  FLAG: { label: 'Check and flag', hint: 'Accept a punch whose face does not match, and flag it for review.' },
  BLOCK: { label: 'Check and block', hint: 'Refuse a face that does not match. After the allowed tries, accept and flag.' },
}

export const FACE_STRICTNESS: Record<FaceStrictness, { label: string; hint: string }> = {
  LOW: { label: 'Low', hint: 'Fewer refusals in poor light; more look-alikes get through.' },
  MEDIUM: { label: 'Medium (recommended)', hint: 'The model’s published balance point.' },
  HIGH: { label: 'High', hint: 'Fewer look-alikes; more genuine people asked to retake.' },
}

export const FACE_RESULTS: Record<FaceResult, { label: string; tone: 'success' | 'warning' | 'neutral' | 'danger' | 'info' }> = {
  MATCHED: { label: 'Face matched', tone: 'success' },
  MISMATCH: { label: 'Face mismatch', tone: 'danger' },
  NO_FACE: { label: 'No face in photo', tone: 'warning' },
  NOT_REGISTERED: { label: 'Face not registered', tone: 'warning' },
  NOT_CHECKED: { label: 'Face not checked', tone: 'neutral' },
  NOT_REQUIRED: { label: 'No face check', tone: 'info' },
}

export const GEOFENCE_MODES: Record<GeofenceMode, { label: string; hint: string }> = {
  OWN_LOCATION: {
    label: 'Own work location only',
    hint: 'The location on their current placement, plus any extra sites you list.',
  },
  SPECIFIC_LOCATIONS: {
    label: 'Specific sites only',
    hint: 'Only the sites you list, whatever their placement says.',
  },
  ANY_LOCATION: {
    label: 'Any company site',
    hint: 'Any active site that has a geofence — for people who move between sites.',
  },
  UNRESTRICTED: {
    label: 'Anywhere (no geofence)',
    hint: 'Field staff, drivers, remote workers. Location is recorded but not checked.',
  },
}

export const GEOFENCE_ENFORCEMENTS: Record<GeofenceEnforcement, { label: string; hint: string }> = {
  BLOCK: { label: 'Block', hint: 'Refuse a punch from outside the fence.' },
  FLAG: { label: 'Allow and flag', hint: 'Accept it, but mark it for review.' },
}

export const GEOFENCE_SCOPES: Record<GeofenceScope, string> = {
  EMPLOYEE: 'Employees',
  DEPARTMENT: 'Departments',
  LOCATION: 'Work locations',
  GRADE: 'Grades',
  LEGAL_ENTITY: 'Legal entities',
}

export const GEOFENCE_RESULTS: Record<GeofenceResult, { label: string; tone: 'success' | 'warning' | 'neutral' | 'danger' | 'info' }> = {
  ACCEPTED: { label: 'Inside fence', tone: 'success' },
  FLAGGED: { label: 'Flagged', tone: 'warning' },
  UNCHECKED: { label: 'Not checked', tone: 'neutral' },
  NOT_REQUIRED: { label: 'No geofence', tone: 'info' },
  OUTSIDE: { label: 'Outside', tone: 'danger' },
}

export interface LocationFenceView {
  id: string
  code: string
  name: string
  city: string | null
  latitude: number | null
  longitude: number | null
  radiusMetres: number | null
  active: boolean
  fenced: boolean
  headcount: number
}

export interface SiteRef {
  id: string
  name: string
  fenced: boolean
  latitude: number | null
  longitude: number | null
  radiusMetres: number | null
}

export interface GeofencePolicyView {
  id: string
  code: string
  name: string
  description: string | null
  mode: GeofenceMode
  enforcement: GeofenceEnforcement
  applyOnPunchIn: boolean
  applyOnPunchOut: boolean
  requireLocation: boolean
  maxAccuracyMetres: number | null
  accuracyTolerance: boolean
  bufferMetres: number
  blockMockLocation: boolean
  allowOutsideWithReason: boolean
  defaultPolicy: boolean
  active: boolean
  locations: SiteRef[]
  assignmentCount: number
  photoMode: PhotoMode
  photoOnPunchIn: boolean
  photoOnPunchOut: boolean
  faceMatch: FaceMatchMode
  faceStrictness: FaceStrictness
  faceMaxAttempts: number
  livenessRequired: boolean
  unenrolledAction: UnenrolledAction
}

export interface SaveGeofencePolicy {
  code: string
  name: string
  description: string | null
  mode: GeofenceMode
  enforcement: GeofenceEnforcement
  applyOnPunchIn: boolean
  applyOnPunchOut: boolean
  requireLocation: boolean
  maxAccuracyMetres: number | null
  accuracyTolerance: boolean
  bufferMetres: number
  blockMockLocation: boolean
  allowOutsideWithReason: boolean
  defaultPolicy: boolean
  active: boolean
  locationIds: string[]
  photoMode: PhotoMode
  photoOnPunchIn: boolean
  photoOnPunchOut: boolean
  faceMatch: FaceMatchMode
  faceStrictness: FaceStrictness
  faceMaxAttempts: number
  livenessRequired: boolean
  unenrolledAction: UnenrolledAction
}

export interface GeofenceAssignmentView {
  id: string
  policyId: string
  policyName: string | null
  scopeType: GeofenceScope
  scopeId: string
  scopeName: string | null
}

export interface EffectivePolicyView {
  employeeId: string
  employeeCode: string
  employeeName: string | null
  departmentName: string | null
  locationName: string | null
  policyId: string | null
  policyName: string | null
  mode: GeofenceMode | null
  enforcement: GeofenceEnforcement | null
  source: string | null
  mandatory: boolean
  allowOutsideWithReason: boolean
  photoRequired: boolean
  faceMatch: FaceMatchMode
  faceRegistered: boolean
  livenessRequired: boolean
  permittedSites: SiteRef[]
  warnings: string[]
}

export interface CoverageSummary {
  employees: number
  mandatory: number
  unrestricted: number
  uncovered: number
  withWarnings: number
  rows: EffectivePolicyView[]
}

export interface PunchReviewView {
  id: string
  employeeId: string
  employeeCode: string | null
  employeeName: string | null
  punchTime: string
  direction: 'IN' | 'OUT'
  source: string
  locationId: string | null
  locationName: string | null
  distanceMetres: number | null
  accuracyMetres: number | null
  latitude: number | null
  longitude: number | null
  result: GeofenceResult
  note: string | null
  outsideReason: string | null
  policyName: string | null
  photoAttached: boolean
  faceResult: FaceResult | null
  faceScore: number | null
  faceNote: string | null
  livenessPassed: boolean | null
}

export interface SelfPunchView {
  id: string
  employeeId: string
  punchTime: string
  direction: 'IN' | 'OUT'
  source: string
  locationId: string | null
  distanceMetres: number | null
  geofenceResult: GeofenceResult
  geofenceNote: string | null
  selfieAttached: boolean
  punchLogId: string | null
  policyId: string | null
  outsideReason: string | null
  faceResult: FaceResult | null
  faceScore: number | null
  faceNote: string | null
}

export interface FaceSettingsView {
  consentText: string
  photoRetentionDays: number
  deleteOnExit: boolean
  engineAvailable: boolean
  modelId: string
}

export interface FaceRegistrationView {
  employeeId: string
  employeeCode: string
  employeeName: string | null
  departmentName: string | null
  registered: boolean
  registeredAt: string | null
  source: 'SELF' | 'HR' | null
  photoCount: number
  faceMatch: FaceMatchMode | null
  policyName: string | null
}

export interface FaceStatus {
  registered: boolean
  enrollmentId: string | null
  registeredAt: string | null
  source: 'SELF' | 'HR' | null
  photoCount: number
  consentText: string
  engineAvailable: boolean
}

/** A link that opens a point in Google Maps in a new tab — the app itself cannot embed a map. */
export function mapLink(latitude: number, longitude: number): string {
  return `https://www.google.com/maps?q=${latitude},${longitude}`
}
